/**
 * Factory BOM API — dedicated cho `/factories/{factoryCode}/bom`. Tách khỏi
 * generic factoryApi vì BOM có sub-form Input/Output children và atomic
 * save flow (parity Access `frmFactoryMDQryBOM_Add btnSave_Click`).
 *
 * Composite business key: `(bom_code, crop_year)`. Detail/update/delete đều
 * cần `crop_year` query param.
 */
import { baseApi } from './baseApi'

export type BomCell = string | number | boolean | null

export interface BomChildRow {
  quality_code: string
  quality_value: number | null
  quality_type?: 'input' | 'output'
  [extra: string]: BomCell | undefined
}

export interface BomListRow {
  bom_code: string
  bom_type: string | null
  crop_year: string | null
  processing_loss: number | null
  moisture_loss: number | null
  note: string | null
  [extra: string]: BomCell | undefined
}

export interface BomListResponse {
  items: BomListRow[]
  total: number
  source: string
}

export interface BomDetail {
  bomCode: string
  bomType: string | null
  cropYear: string | null
  processingLoss: number | null
  moistureLoss: number | null
  note: string | null
  inputs: BomChildRow[]
  outputs: BomChildRow[]
  extra: Record<string, BomCell>
}

export interface BomQualityRef {
  code: string
  liffe: number | null
  diff: number | null
  fx: number | null
  price_from_g2: number | null
  date_actual: string | null
  price_unit: string | null
}

export interface BomQualityRefItem {
  code: string
  liffe: number | null
  diff: number | null
  premium: number | null
  value: number | null
  fx: number | null
  price: number | null
  price_from_g2: number | null
}

export interface BomQualityRefsResponse {
  items: BomQualityRefItem[]
  total: number
}

export interface BomOptions {
  bomTypes: string[]
  qualityItems: Array<{ code: string; [k: string]: BomCell }>
  cropYears: string[]
  currentCropYear: string | null
  nextCropYear: string | null
}

/** Combo `bom_crop` ở form PO List: crop có recipe cho bom_code + crop suggest theo PO date. */
export interface BomCropOptions {
  cropYears: string[]
  suggested: string | null
}

/** Batch report persistence (DELTA) lưu trên `po`. Xem project-bom-bta1. */
export interface BomEditDelta {
  liffe?: number
  g2_diffs?: number
  /** Recipe Default in/out FROZEN (chỉ có khi Finished) — khoá % In/Out. */
  bom?: Record<string, { in?: number | null; out?: number | null }>
  /** Sparse — chỉ quality code có Edit; in/out là phân số (0.40 = 40%). */
  items?: Record<string, { in?: number | null; out?: number | null }>
}
/** Frozen cost ({cost, unitCosts, fx}) — chỉ có khi Finished (Phương án B). */
export interface CostFrozen {
  cost: Record<string, number> | null
  unitCosts: Array<{
    cost_item_code: string
    unit_price: number | null
    valid_from: string | null
    valid_to: string | null
    in_effect: boolean
  }>
  fx: number | null
}
export interface CostEditDelta {
  /** V/C transport manual (VND/ton) — chưa có nguồn BE. */
  vc_hopper?: number
  vc_warehouse?: number
  /** Bag mix (tons) BB vs PP/JB → volume-weight đơn giá Hopper/Packing. */
  bb_tons?: number
  ppjb_tons?: number
  /** Tấn theo hình thức nhập (Pallet/Stack/Processing) → weighted đơn giá Intake. */
  pallet_tons?: number
  stack_tons?: number
  proc_tons?: number
  /** Số công tơ điện nhập theo từng PO (kWh). submeter = công tơ phụ trừ ra (điện
   *  không thuộc mẻ); extra_deduct = khấu trừ thêm. kWh = (meter_new−meter_old) −
   *  (submeter_new−submeter_old) − extra_deduct. */
  power?: {
    meter_new?: number
    meter_old?: number
    submeter_new?: number
    submeter_old?: number
    extra_deduct?: number
  }
}
export interface BatchReportPersist {
  bomCrop: string | null
  isFinished: boolean // GỘP: true = Finished (BR chốt = PO đóng), false = Processing
  isBatchStandard: number // 0 = Non-std (chưa quy chuẩn), 1 = Std (quy chuẩn)
  bomEdit: BomEditDelta | null
  costEdit: CostEditDelta | null
  /** Frozen cost — chỉ có khi Finished; null → FE tính live. */
  costFrozen: CostFrozen | null
  /** Frozen standard per-item ({quality_premium, price_vs_g2, fx, mc...}) — khi Finished. */
  standardFrozen: Record<string, Record<string, number | null>> | null
  compareEdit: CompareEditDelta | null
  /** Comparison tab "Compare with" PO No — persist trong bom_edit.compare_with. */
  compareWith: string | null
}
export interface BatchReportSaveBody {
  bomCrop?: string | null
  bomEdit?: BomEditDelta | null
  costEdit?: CostEditDelta | null
  compareEdit?: CompareEditDelta | null
}

/** Delta Comparison tab (③ Narrative) — note/standardized nhập tay theo item.
 *  Persist ở po.compare_edit: {items:{<itemCode>:{note?,standardized?}}}. */
export interface CompareEditDelta {
  items?: Record<string, { note?: string | null; standardized?: string | null }>
}

/** Standardization record (tlc_factory.po_standard) — 4 field nhập tay + audit. */
export interface StandardizationRow {
  id: number
  factoryCode: string | null
  poNo: string | null
  stackBefore: string | null
  grnBefore: string | null
  deductRate: number | null
  stackAfter: string | null
  grnAfter: string | null
  reason: string | null
  /** Computed từ view v_batchreport_standard. */
  stackBeforeItem: string | null
  stackBeforeMt: number | null
  deductValue: number | null
  stackAfterItem: string | null
  /** Computed quality (std threshold + after) — các cột sau Reason. */
  blackStd: number | null
  brokensStd: number | null
  fmStd: number | null
  s18Std: number | null
  s16Std: number | null
  s13Std: number | null
  brownStd: number | null
  blackAfterStd: number | null
  brokensAfterStd: number | null
  fmAfterStd: number | null
  s18AfterStd: number | null
  s16AfterStd: number | null
  s13AfterStd: number | null
  brownAfterStd: number | null
  createdDate: string | null
  createdBy: string | null
  updatedDate: string | null
  updatedBy: string | null
}
export interface StandardizationSaveBody {
  stackBefore?: string | null
  grnBefore?: string | null
  deductRate?: number | null
  stackAfter?: string | null
  grnAfter?: string | null
  reason?: string | null
}

/** 1 dòng tab "Item": distinct item của PO + phase/category (list_and_valo) +
 *  mc/blacks/fm (po.standard_edit). Sort sẵn theo order_by (BE). */
export interface BatchReportItemRow {
  poNo: string | null
  item: string | null
  phase: string | null
  category: string | null
  mc: number | null
  blacks: number | null
  brokens: number | null
  fm: number | null
}

/** 1 dòng block "Other info" (read-only). Standard MC/Blacks/Brokens/FM lấy từ
 *  deduction_standard_detail (factory-level) + quality_premium/price_from_g2/fx. */
export interface PoItemInfoRow {
  poNo: string | null
  item: string | null
  phase: string | null
  description: string | null
  mc: number | null
  blacks: number | null
  brokens: number | null
  brown: number | null
  fm: number | null
  qualityPremium: number | null
  priceVsG2: number | null
  fx: number | null
}

/** 1 dòng bảng Rejects/Byproduct (tab Other info). value frozen khi PO Finished. */
export interface RejectsByproductRow {
  parameterCode: string | null
  fieldName: string | null
  rejectedByproduct: number | null
}

/** (Stack, GRN) của PO (view v_batchreport_receive) — combo Stack + GRN before/after.
 *  Grain = (stackId, grnId): 1 stack có thể nhận từ nhiều GRN. */
export interface PoStack {
  stackId: string
  grnId: string | null
  item: string | null
  area: string | null
  mt: number | null
}

export interface BomSavePayload {
  bom_code: string
  bom_type: string
  crop_year: string
  processing_loss?: number | null
  moisture_loss?: number | null
  note?: string | null
  inputs: Array<{ quality_code: string; quality_value?: number | null }>
  outputs: Array<{ quality_code: string; quality_value?: number | null }>
}

const bomUrl = (factoryCode: string, ...segments: string[]): string =>
  ['/factories', factoryCode, 'bom', ...segments].filter(Boolean).join('/')

/* ---- Batch Report Dashboard (aggregation từ 2 view) --------------------- */
export interface BatchReportDashboardKpis {
  inputNetWeight: number
  outputNetWeight: number
  outTurn: number
  loss: number
  totalValueIn: number
  totalValueOut: number
  valueExtracted: number
  valueExtractedPerMt: number
}
export interface BatchReportDashboard {
  kpis: BatchReportDashboardKpis
  outputCategoryDistribution: Array<{ category: string; mt: number }>
  outputItemsMt: Array<{ item: string; mt: number; category?: string }>
  categoryActualVsBom: Array<{ label: string; actual: number; bom: number; category?: string }>
  itemActualVsBom: Array<{ label: string; actual: number; bom: number; category?: string }>
  qualityDefects: Array<{ label: string; input: number; output: number; diff: number }>
  qualityScreen: Array<{ label: string; input: number; output: number; diff: number }>
  valueInByCategory: Array<{ category: string; value: number }>
  valueOutByCategory: Array<{ category: string; value: number }>
}

/** Production Analysis aggregation (2 view + BOM recipe) — rows shape khớp
 *  BudgetActualRow; loss/extracted khớp LossSummary/ExtractedValueSummary (FE). */
export interface ProductionAnalysisResult {
  inputRows: Array<Record<string, unknown>>
  outputRows: Array<Record<string, unknown>>
  loss: Record<string, number>
  extracted: Record<string, number>
  totalInputMt: number
  /** Source = distinct origin_source ở INPUT (BE join ' | '). */
  source: string
}

/** Comparison aggregation (sheet `Comparison`) — current PO vs comparison PO.
 *  Số liệu tính live từ 2 view + reuse Production Analysis (xem
 *  plan/production/BATCH_REPORT_COMPARISON_DATA_SPEC.md). note/standardized là
 *  free-text analyst (chưa wire store → rỗng). previous/current/variance là fraction. */
export interface YieldComparisonRow {
  item: string
  category: string
  premium: number
  previous: number
  current: number
  variance: number
}
export interface FaqQualityRow {
  po: string
  mc: number; blacks: number; brokens: number; bb: number; fm: number
  mouldy: number; brown: number; partlyBlack: number; immature: number
  s18: number; s16: number; s13: number
}
export interface ComparisonNarrativeRow {
  item: string
  /** Category (phase) của item — tag hiển thị (Graded/By-products/Rejects/FAQ/…). */
  category: string
  mc: number; bb: number; fm: number; s18: number; s16: number; s13: number
  note: string
  standardized: string
}
export interface ComparisonPriceInfo {
  liffe: number | null
  g2Diffs: number
  diffLabel: string
  priceDate: string | null
  premiumByItem: Array<{ item: string; premium: number }>
}
export interface ComparisonProductionInfo {
  totalInput: number
  totalOutturnMt: number
  loss: number
  mcLoss: number
  unexplainedLoss: number
}
/** PnL (Extracted value) — shape khớp get_production_analysis["extracted"]. */
export interface ComparisonPnlInfo {
  valueIn: number
  valueOut: number
  valueExtracted: number
  valueExtractedPerMt: number
  handling: number
  valueExtractedPerMtAfterCost: number
  actualExtracted: number
  bomExtracted: number
  pnlPerMt: number
  totalPnl: number
}
export interface BatchReportComparison {
  currentPoNo: string
  comparisonPoNo: string
  yieldComparison: YieldComparisonRow[]
  faqQuality: FaqQualityRow[]
  narrative: ComparisonNarrativeRow[]
  priceInfo: ComparisonPriceInfo
  productionInfo: ComparisonProductionInfo
  /** Price/Production của comparison PO — block ④/⑤ so sánh cả 2 PO. */
  priceInfoCompare: ComparisonPriceInfo
  productionInfoCompare: ComparisonProductionInfo
  /** PnL (Extracted value) — current + comparison PO (block ⑥). */
  pnlInfo: ComparisonPnlInfo
  pnlInfoCompare: ComparisonPnlInfo
}

export const factoryBomApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFactoryBomList: builder.query<
      BomListResponse,
      { factoryCode: string; cropYear?: string }
    >({
      query: ({ factoryCode, cropYear }) => ({
        url: bomUrl(factoryCode),
        params: cropYear ? { crop_year: cropYear } : undefined,
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:list:${arg.cropYear ?? 'all'}` },
      ],
    }),

    getFactoryBomOptions: builder.query<BomOptions, string>({
      query: (factoryCode) => bomUrl(factoryCode, 'options'),
      providesTags: (_r, _e, factoryCode) => [
        { type: 'FactoryBom', id: `${factoryCode}:options` },
      ],
    }),

    getBomQualityRef: builder.query<
      BomQualityRef,
      { factoryCode: string; code: string; date?: string }
    >({
      query: ({ factoryCode, code, date }) => ({
        url: bomUrl(factoryCode, 'quality-ref'),
        params: date ? { code, date } : { code },
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:qref:${arg.code}:${arg.date ?? 'now'}` },
      ],
    }),

    getBomCropOptions: builder.query<
      BomCropOptions,
      { factoryCode: string; bomCode: string; date?: string }
    >({
      query: ({ factoryCode, bomCode, date }) => ({
        url: bomUrl(factoryCode, 'crop-options'),
        params: date ? { bom_code: bomCode, date } : { bom_code: bomCode },
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:cropopt:${arg.bomCode}:${arg.date ?? 'nodate'}` },
      ],
    }),

    getBomQualityRefs: builder.query<
      BomQualityRefsResponse,
      { factoryCode: string; date?: string }
    >({
      query: ({ factoryCode, date }) => ({
        url: bomUrl(factoryCode, 'quality-refs'),
        params: date ? { date } : undefined,
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:qrefs:${arg.date ?? 'now'}` },
      ],
    }),

    getBagMix: builder.query<
      {
        bb_tons: number
        ppjb_tons: number
        pallet_tons: number
        stack_tons: number
        proc_tons: number
      },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'bag-mix'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:bagmix:${arg.poNo}` },
      ],
    }),

    getBatchReportPersist: builder.query<
      BatchReportPersist,
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo)].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:br:${arg.poNo}` },
      ],
    }),

    saveBatchReport: builder.mutation<
      BatchReportPersist,
      { factoryCode: string; poNo: string; body: BatchReportSaveBody }
    >({
      query: ({ factoryCode, poNo, body }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo)].join('/'),
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:br:${arg.poNo}` },
      ],
    }),

    // Đổi cờ trạng thái: isFinished true = Finished (PO đóng), false = Processing.
    setBatchReportStatus: builder.mutation<
      BatchReportPersist,
      { factoryCode: string; poNo: string; isFinished: boolean; poClossingDate?: string | null }
    >({
      query: ({ factoryCode, poNo, isFinished, poClossingDate }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'status'].join('/'),
        method: 'PUT',
        body: { isFinished, poClossingDate: poClossingDate ?? null },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:br:${arg.poNo}` },
        // PO List hiển thị PO → refresh để trạng thái mới phản ánh ngay.
        { type: 'Factory', id: `${arg.factoryCode}:po-list` },
      ],
    }),

    // Đổi cờ quy chuẩn: isBatchStandard 1 = Std (quy chuẩn), 0 = Non-std (chưa).
    setBatchReportStandard: builder.mutation<
      BatchReportPersist,
      { factoryCode: string; poNo: string; isBatchStandard: 0 | 1 }
    >({
      query: ({ factoryCode, poNo, isBatchStandard }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'standard'].join('/'),
        method: 'PUT',
        body: { isBatchStandard },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:br:${arg.poNo}` },
        { type: 'Factory', id: `${arg.factoryCode}:po-list` },
      ],
    }),

    // Comparison tab — lưu "Compare with" PO vào bom_edit.compare_with (merge,
    // không đụng delta BOM). Invalidate persist tag → GET load lại value đúng.
    setBatchReportCompareWith: builder.mutation<
      BatchReportPersist,
      { factoryCode: string; poNo: string; compareWith: string }
    >({
      query: ({ factoryCode, poNo, compareWith }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'compare-with'].join('/'),
        method: 'PUT',
        body: { compareWith },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:br:${arg.poNo}` },
      ],
    }),

    getBatchReportInput: builder.query<
      { items: Array<Record<string, unknown>> },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'input'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:brinput:${arg.poNo}` },
      ],
    }),

    getBatchReportOutput: builder.query<
      { items: Array<Record<string, unknown>> },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'output'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:broutput:${arg.poNo}` },
      ],
    }),

    getBatchReportDashboard: builder.query<
      BatchReportDashboard,
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'dashboard'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:brdash:${arg.poNo}` },
      ],
    }),

    getProductionAnalysis: builder.query<
      ProductionAnalysisResult,
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'production-analysis'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:brpa:${arg.poNo}` },
      ],
    }),

    getBatchReportComparison: builder.query<
      BatchReportComparison,
      { factoryCode: string; poNo: string; comparisonPoNo: string }
    >({
      query: ({ factoryCode, poNo, comparisonPoNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'comparison'].join('/'),
        params: comparisonPoNo ? { comparison_po_no: comparisonPoNo } : undefined,
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:brcmp:${arg.poNo}:${arg.comparisonPoNo}` },
      ],
    }),

    getPoStacks: builder.query<
      { items: PoStack[] },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'stacks'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:stacks:${arg.poNo}` },
      ],
    }),

    // Tab "Item": distinct item (issue ∪ receive) + list_and_valo + po.standard_edit.
    getBatchReportItems: builder.query<
      { items: BatchReportItemRow[] },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'items'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:items:${arg.poNo}` },
      ],
    }),
    // Block "Other info" (read-only): is_finished=false → live (deduction standard
    // factory-level + premium/price/fx); is_finished=true → đọc frozen po.standard_edit.
    // rejectsByproduct cùng quy tắc (frozen value khi Finished).
    getBatchReportItemInfo: builder.query<
      { items: PoItemInfoRow[]; rejectsByproduct: RejectsByproductRow[] },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'item-info'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:item-info:${arg.poNo}` },
      ],
    }),
    // Edit 1 field Standard (mc/blacks/fm) của 1 item → po.standard_edit[item][field].
    saveBatchReportItemStandard: builder.mutation<
      Partial<BatchReportItemRow>,
      { factoryCode: string; poNo: string; item: string; field: 'mc' | 'blacks' | 'brokens' | 'fm'; value: number | null }
    >({
      query: ({ factoryCode, poNo, item, field, value }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'items', 'standard'].join('/'),
        method: 'PUT',
        body: { item, field, value },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:items:${arg.poNo}` },
      ],
    }),

    // ---- po_standard CRUD (Standardization records) ----
    getStandardizationRows: builder.query<
      { items: StandardizationRow[] },
      { factoryCode: string; poNo: string }
    >({
      query: ({ factoryCode, poNo }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'standardization'].join('/'),
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:std:${arg.poNo}` },
      ],
    }),
    createStandardization: builder.mutation<
      StandardizationRow,
      { factoryCode: string; poNo: string; body: StandardizationSaveBody }
    >({
      query: ({ factoryCode, poNo, body }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'standardization'].join('/'),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:std:${arg.poNo}` },
      ],
    }),
    updateStandardization: builder.mutation<
      StandardizationRow,
      { factoryCode: string; poNo: string; id: number; body: StandardizationSaveBody }
    >({
      query: ({ factoryCode, poNo, id, body }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'standardization', String(id)].join('/'),
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:std:${arg.poNo}` },
      ],
    }),
    deleteStandardization: builder.mutation<
      { id: number; deleted: boolean },
      { factoryCode: string; poNo: string; id: number }
    >({
      query: ({ factoryCode, poNo, id }) => ({
        url: ['/factories', factoryCode, 'batch-report', encodeURIComponent(poNo), 'standardization', String(id)].join('/'),
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:std:${arg.poNo}` },
      ],
    }),

    getFactoryBomDetail: builder.query<
      BomDetail,
      { factoryCode: string; bomCode: string; cropYear: string }
    >({
      query: ({ factoryCode, bomCode, cropYear }) => ({
        url: bomUrl(factoryCode, encodeURIComponent(bomCode)),
        params: { crop_year: cropYear },
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:${arg.cropYear}:${arg.bomCode}` },
      ],
    }),

    createFactoryBom: builder.mutation<
      BomDetail,
      { factoryCode: string; body: BomSavePayload }
    >({
      query: ({ factoryCode, body }) => ({
        url: bomUrl(factoryCode),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:list:all` },
        { type: 'FactoryBom', id: `${arg.factoryCode}:list:${arg.body.crop_year}` },
      ],
    }),

    updateFactoryBom: builder.mutation<
      BomDetail,
      { factoryCode: string; bomCode: string; cropYear: string; body: BomSavePayload }
    >({
      query: ({ factoryCode, bomCode, cropYear, body }) => ({
        url: bomUrl(factoryCode, encodeURIComponent(bomCode)),
        method: 'PUT',
        params: { crop_year: cropYear },
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:list:all` },
        { type: 'FactoryBom', id: `${arg.factoryCode}:list:${arg.cropYear}` },
        { type: 'FactoryBom', id: `${arg.factoryCode}:${arg.cropYear}:${arg.bomCode}` },
      ],
    }),

    deleteFactoryBom: builder.mutation<
      void,
      { factoryCode: string; bomCode: string; cropYear: string }
    >({
      query: ({ factoryCode, bomCode, cropYear }) => ({
        url: bomUrl(factoryCode, encodeURIComponent(bomCode)),
        method: 'DELETE',
        params: { crop_year: cropYear },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'FactoryBom', id: `${arg.factoryCode}:list:all` },
        { type: 'FactoryBom', id: `${arg.factoryCode}:list:${arg.cropYear}` },
        { type: 'FactoryBom', id: `${arg.factoryCode}:${arg.cropYear}:${arg.bomCode}` },
      ],
    }),
  }),
})

export const {
  useGetFactoryBomListQuery,
  useGetFactoryBomOptionsQuery,
  useGetBomCropOptionsQuery,
  useGetBagMixQuery,
  useGetBatchReportPersistQuery,
  useSaveBatchReportMutation,
  useSetBatchReportStatusMutation,
  useSetBatchReportStandardMutation,
  useSetBatchReportCompareWithMutation,
  useGetBatchReportInputQuery,
  useGetBatchReportOutputQuery,
  useGetBatchReportDashboardQuery,
  useGetProductionAnalysisQuery,
  useGetBatchReportComparisonQuery,
  useGetPoStacksQuery,
  useGetBatchReportItemsQuery,
  useLazyGetBatchReportItemsQuery,
  useGetBatchReportItemInfoQuery,
  useSaveBatchReportItemStandardMutation,
  useGetStandardizationRowsQuery,
  useCreateStandardizationMutation,
  useUpdateStandardizationMutation,
  useDeleteStandardizationMutation,
  useGetFactoryBomDetailQuery,
  useGetBomQualityRefQuery,
  useGetBomQualityRefsQuery,
  useCreateFactoryBomMutation,
  useUpdateFactoryBomMutation,
  useDeleteFactoryBomMutation,
} = factoryBomApi
